import axios, { AxiosHeaders } from "axios";

const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);

const getCookieValue = (name: string): string | null => {
    if (typeof document === "undefined") {
        return null;
    }
    const value = document.cookie
        ?.split(";")
        .map((cookie) => cookie.trim())
        .find((cookie) => cookie.startsWith(`${name}=`));
    return value ? decodeURIComponent(value.split("=")[1]) : null;
};

const api = axios.create({
    baseURL: process.env.NEXT_PUBLIC_API_BASE_URL, // Ensure this is correct
    timeout: 10000,
    headers: {
        "Content-Type": "application/json",
    },
    withCredentials: true, // ✅ Ensures cookies (including auth token) are sent automatically
});

api.interceptors.request.use(
    (config) => {
        const method = config.method?.toLowerCase() ?? "get";
        if (MUTATING_METHODS.has(method)) {
            const csrfToken = getCookieValue("csrf_access_token");
            if (csrfToken) {
                const headers =
                    config.headers instanceof AxiosHeaders
                        ? config.headers
                        : new AxiosHeaders(config.headers ?? {});
                headers.set("X-CSRF-TOKEN", csrfToken);
                config.headers = headers;
            }
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Login function
interface LoginResponse {
    message: string;
    token?: string;
    redirect_to?: string;
    course_id?: string;
}

interface AdminLoginPayload {
    email: string;
    password: string;
}

export const loginStudent = async (
    email: string,
    password: string
): Promise<LoginResponse> => {
    try {
        const response = await api.post<LoginResponse>("/auth/login", {
            email,
            password,
        });
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

interface DemoSignupPayload {
    name: string;
    email: string;
    phone_number: string;
    password: string;
    course_id: string;
}

export const signupDemoStudent = async (
    payload: DemoSignupPayload
): Promise<LoginResponse> => {
    try {
        const response = await api.post<LoginResponse>("/auth/self-signup/demo", payload);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const loginAdmin = async (
    email: string,
    password: string
): Promise<LoginResponse> => {
    const payload: AdminLoginPayload = { email, password };
    try {
        const response = await api.post<LoginResponse>("/auth/ganesha@2024/admin/login", payload);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export interface LogoutResponse {
    message: string;
}

export const getStudentDetail = async () => {
    try {
        const response = await api.get("/users/user"); // ✅ Cookies will handle auth

        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const getCourseDetail = async (course_id: string) => {
    try {
        const response = await api.get(`/courses/${course_id}`);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const getPublicCourseDetail = async (course_id: string) => {
    try {
        const response = await api.get(`/courses/public/${course_id}`);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const getDemoCourses = async () => {
    try {
        const response = await api.get("/courses/demo-options");
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const getPaidCourses = async () => {
    try {
        const response = await api.get("/courses/paid-options");
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const logoutStudent = async (): Promise<LogoutResponse> => {
    try {
        const response = await api.post<LogoutResponse>("/auth/logout");
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const stream_video = async (
    Course_id: string,
    title: string,
    video_name: string
): Promise<any> => {
    try {
        const response = await api.get(
            `/courses/${Course_id}/${title}/${video_name}`
        ); // ✅ Send `title` in the request body
        return response.data;
    } catch (error: any) {
        throw error;
    }
};
export const get_comments = async (
    course_id: string,
    video_title: string
): Promise<any> => {
    try {
        const response = await api.get(
            `/comment/courses/${course_id}/${video_title}/comments`
        );
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const post_comments = async (
    course_id: string,
    selectedTopic: string,
    video_title: string,
    comment: string
) => {
    try {
        const response = await api.post(
            `/comment/courses/${course_id}/${selectedTopic}/${video_title}/comments`,
            {
                text: comment, // This matches your API's expected format
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const check_auth = async () => {
    try {
        const response = await api.get("/auth/");
        return response;
    } catch (error) {
        return null;
    }
};

interface LoginHelpPayload {
    name: string;
    email: string;
    number: string;
    message?: string;
}

export const requestLoginHelp = async (payload: LoginHelpPayload) => {
    try {
        const response = await api.post("/enquiry/login-help", payload);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

interface FullSignupOrderPayload {
    name: string;
    email: string;
    phone_number: string;
    password: string;
    course_id: string;
}

export const createFullSignupOrder = async (payload: FullSignupOrderPayload) => {
    try {
        const response = await api.post("/auth/self-signup/full/order", payload);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

interface VerifyFullSignupPaymentPayload {
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
}

export const verifyFullSignupPayment = async (
    payload: VerifyFullSignupPaymentPayload
) => {
    try {
        const response = await api.post("/auth/self-signup/full/verify", payload);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const getBlogs = async () => {
    try {
        const response = await api.get("/blogs");
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const getBlogCategories = async () => {
    try {
        const response = await api.get("/blogs/categories");
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const getBlogById = async (id: string) => {
    try {
        const response = await api.get(`/blogs/${id}`);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

interface CreateBlogPayload {
    title: string;
    short_description: string;
    category: string | string[];
    sub_category?: string | string[];
    author: string;
    cover_image?: File | null;
    content: string;
}

const appendStringOrArray = (
    formData: FormData,
    key: string,
    value: string | string[] | undefined
) => {
    if (Array.isArray(value)) {
        const cleaned = value.map((item) => String(item || "").trim()).filter(Boolean);
        formData.append(key, JSON.stringify(cleaned));
        cleaned.forEach((item) => formData.append(`${key}[]`, item));
        return;
    }
    formData.append(key, String(value || "").trim());
};

export const createBlog = async (payload: CreateBlogPayload) => {
    try {
        const formData = new FormData();
        formData.append("title", payload.title);
        formData.append("short_description", payload.short_description);
        appendStringOrArray(formData, "category", payload.category);
        appendStringOrArray(formData, "sub_category", payload.sub_category);
        formData.append("author", payload.author);
        if (payload.cover_image) {
            formData.append("cover_image", payload.cover_image);
        }
        formData.append("content", payload.content);

        const response = await api.post("/blogs/create", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

interface UpdateBlogPayload {
    title: string;
    short_description: string;
    category: string | string[];
    sub_category?: string | string[];
    author: string;
    cover_image?: File | null;
    content: string;
}

export const updateBlog = async (id: string, payload: UpdateBlogPayload) => {
    try {
        const formData = new FormData();
        formData.append("title", payload.title);
        formData.append("short_description", payload.short_description);
        appendStringOrArray(formData, "category", payload.category);
        appendStringOrArray(formData, "sub_category", payload.sub_category);
        formData.append("author", payload.author);
        formData.append("content", payload.content);
        if (payload.cover_image) {
            formData.append("cover_image", payload.cover_image);
        }

        const response = await api.put(`/blogs/${id}/update`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const deleteBlog = async (id: string) => {
    try {
        const response = await api.delete(`/blogs/${id}`);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

interface CreateCoursePayload {
    title: string;
    description?: string;
    price: string | number;
    short_description?: string;
    essential_features: string;
    is_discounted?: boolean;
    discount_price?: string | number;
    starting_date?: string;
    batch_type?: string;
    batch_days?: string[];
    batch_time?: string;
    few_seats_left?: boolean;
    display?: boolean;
    cover_image: File;
    subtitles?: any[];
}

interface UpdateCoursePayload {
    title: string;
    description?: string;
    price: string | number;
    short_description?: string;
    essential_features: string;
    is_discounted?: boolean;
    discount_price?: string | number;
    starting_date?: string;
    batch_type?: string;
    batch_days?: string[];
    batch_time?: string;
    few_seats_left?: boolean;
    display?: boolean;
    cover_image?: File | null;
    subtitles?: any[];
}

const buildCoursePayload = (
    payload: CreateCoursePayload | UpdateCoursePayload
) => {
    const data: any = {
        title: payload.title,
        description: payload.description || "",
        price: String(payload.price ?? ""),
        short_description: payload.short_description || "",
        is_discounted: Boolean(payload.is_discounted),
        discount_price:
            payload.is_discounted && payload.discount_price !== undefined && payload.discount_price !== null
                ? String(payload.discount_price)
                : "",
        starting_date: payload.starting_date || "",
        batch_type: payload.batch_type || "",
        batch_days: Array.isArray(payload.batch_days) ? payload.batch_days : [],
        batch_time: payload.batch_time || "",
        few_seats_left: Boolean(payload.few_seats_left),
        essential_features: payload.essential_features || "",
        display: Boolean(payload.display),
    };
    if (payload.subtitles !== undefined) {
        data.subtitles = payload.subtitles;
    }
    return data;
};

export const getCoursesAdmin = async () => {
    try {
        // /courses/all returns ALL courses regardless of display flag — admin only
        const response = await api.get("/courses/all");
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const createCourse = async (payload: CreateCoursePayload) => {
    try {
        const coursePayload = buildCoursePayload(payload);
        const formData = new FormData();
        formData.append("title", coursePayload.title);
        formData.append("description", coursePayload.description);
        formData.append("subtitles", JSON.stringify(coursePayload.subtitles));
        formData.append("price", coursePayload.price);
        formData.append("short_description", coursePayload.short_description);
        formData.append("is_discounted", String(coursePayload.is_discounted));
        formData.append("discount_price", coursePayload.discount_price);
        formData.append("starting_date", coursePayload.starting_date);
        formData.append("batch_type", coursePayload.batch_type);
        formData.append("batch_days", JSON.stringify(coursePayload.batch_days));
        formData.append("batch_time", coursePayload.batch_time);
        formData.append("few_seats_left", String(coursePayload.few_seats_left));
        formData.append("essential_features", coursePayload.essential_features);
        formData.append("display", String(coursePayload.display));
        formData.append("cover_image", payload.cover_image);

        const response = await api.post("/courses/create_course", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const updateCourse = async (id: string, payload: UpdateCoursePayload) => {
    try {
        const coursePayload = buildCoursePayload(payload);
        const formData = new FormData();
        formData.append("course_data", JSON.stringify(coursePayload));
        if (payload.cover_image) {
            formData.append("cover_image", payload.cover_image);
        }

        const response = await api.put(`/courses/${id}/update`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const deleteCourse = async (id: string) => {
    try {
        const response = await api.delete(`/courses/${id}`);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const postBlogComment = async (
    blogId: string,
    comment: string,
    guestName?: string,
    isLoggedIn?: boolean
) => {
    const payload: Record<string, string> = {
        text: comment,
    };

    if (guestName) {
        payload.user_name = guestName;
        payload.name = guestName;
    }

    const response = await api.post(
        `/blogs/${blogId}/comments`,
        payload,
        {
            withCredentials: !!isLoggedIn, // ✅ KEY FIX
        }
    );

    return response.data;
};


export const getBlogComments = async (blogId: string) => {
    try {
        const response = await api.get(`/blogs/${blogId}/comments`);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const contact_us = async (data: any) => {
    try {
        const response = await api.post("/enquiry/contact-us", {
            data,
        });

        return response;
    } catch (error) {
        return error;
    }
};

export const save_video_progress = async (
    course_id: string,
    subtitle_title: string,
    video_name: string,
    position_seconds: number,
    duration_seconds: number
) => {
    try {
        const response = await api.post("/courses/progress", {
            course_id,
            subtitle_title,
            video_name,
            position_seconds,
            duration_seconds,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const get_course_progress = async (course_id: string) => {
    try {
        const response = await api.get(`/courses/${course_id}/progress`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getEnquiries = async () => {
    try {
        const response = await api.get("/enquiry/enquiries");
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const deleteEnquiry = async (id: string) => {
    try {
        const response = await api.delete(`/enquiry/enquiries/${id}`);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const markEnquiryRead = async (id: string, read: boolean) => {
    try {
        const response = await api.patch(`/enquiry/enquiries/${id}/read`, { read });
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const markAllEnquiriesRead = async () => {
    try {
        const response = await api.patch("/enquiry/enquiries/mark-all-read");
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const getStudentsList = async () => {
    try {
        const response = await api.get("/users/");
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const updateStudent = async (id: string, payload: { name: string; email: string; phone_number?: string; role?: string }) => {
    try {
        const response = await api.put(`/users/${id}`, payload);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const deleteStudent = async (id: string) => {
    try {
        const response = await api.delete(`/users/${id}`);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const enrollStudentInCourse = async (studentId: string, courseId: string) => {
    try {
        const response = await api.post(`/users/${studentId}/${courseId}`);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const removeStudentFromCourse = async (studentId: string, courseId: string) => {
    try {
        const response = await api.delete(`/users/${studentId}/course/${courseId}`);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const getStudentProgress = async (studentId: string, courseId: string) => {
    try {
        const response = await api.get(`/users/${studentId}/progress/${courseId}`);
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const getConsentStatus = async () => {
    try {
        const response = await api.get("/consent/status");
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const saveConsents = async (consents: string[]) => {
    try {
        const response = await api.post("/consent/save", { consents });
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const withdrawConsent = async (consent: string) => {
    try {
        const response = await api.post("/consent/withdraw", { consent });
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export default api;

